/**
 * The single source of truth for the two-tier staff navigation. AppHeader
 * renders entirely from this — section structure lives nowhere else.
 *
 * Only change to destinations vs the old flat header: Templates moves from a
 * top-level item to the second sub-link under Quotes (it's a quotes template).
 * Active state is derived from the URL (see helpers below), never local state,
 * so it's correct on deep-link / refresh / back.
 */

export interface SubLink {
  label: string;
  /** Exact route this sub-link lands on. */
  path: string;
  /** Contextual create-button text shown ONLY when this sub is the active view
   *  (e.g. "New quote" on the Quotes view, "New template" on Templates). The
   *  button routes to this sub's own path carrying { create: true }. */
  newLabel?: string;
  /** A second create action shown next to `newLabel` that simply navigates to a
   *  path (no { create: true } flow) — e.g. "New from template" on Quotes. It
   *  renders as the primary button; `newLabel` then renders as secondary. */
  altNew?: { label: string; path: string };
  /** Row-2 placement. "right" pushes the link past the flexible spacer,
   *  visually separating occasional utilities (Chat › Import History) from the
   *  everyday views. Active-state handling is unaffected — the link stays in
   *  `subs`, so activeSub keeps working. */
  align?: "right";
}

export interface NavSection {
  /** Stable id. */
  key: string;
  /** Row-1 text. */
  label: string;
  /** Where a top-level click lands (normally === subs[0].path). */
  defaultPath: string;
  /** Row-2 links; may be a single item. */
  subs: SubLink[];
  /** Path prefixes that mark this section active. Defaults to the subs' paths,
   *  which is what lets Quotes own both /staff/quotes and /staff/templates. */
  matchPrefixes?: string[];
  /** Admin-only section: hidden from the header for role "staff", and its
   *  routes are not mounted (StaffApp). Every other section is open to both
   *  console roles. */
  adminOnly?: boolean;
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: "overview",
    label: "Overview",
    defaultPath: "/staff/overview",
    // Studio overview dashboard — read-only, no create action.
    subs: [{ label: "Overview", path: "/staff/overview" }],
  },
  {
    key: "chat",
    label: "Chat",
    defaultPath: "/staff/chat",
    // Client-portal counterpart (Staff Chat Brief 01): per-job messaging and
    // document-share management, iPad split-view pattern web-sized. The badge
    // is the total unread across jobs, fed by the chat feed context.
    subs: [
      { label: "Messages", path: "/staff/chat" },
      { label: "Documents", path: "/staff/chat/documents" },
      // Right-aligned: a migration utility, not an everyday view.
      { label: "Import History", path: "/staff/chat/import-history", align: "right" },
    ],
  },
  {
    key: "leads",
    label: "Leads",
    defaultPath: "/staff/leads",
    // Pipeline is the kanban board (default landing); List is the same working
    // set as a filterable table. Both open the lead drawer and both can create.
    subs: [
      { label: "Pipeline", path: "/staff/leads", newLabel: "New lead" },
      { label: "List", path: "/staff/leads/list", newLabel: "New lead" },
    ],
  },
  {
    key: "quotes",
    label: "Quotes",
    defaultPath: "/staff/quotes",
    subs: [
      {
        label: "Quotes",
        path: "/staff/quotes",
        newLabel: "New quote",
        altNew: { label: "New from template", path: "/staff/templates" },
      },
      { label: "Accepted", path: "/staff/quotes/accepted" },
      { label: "Templates", path: "/staff/templates", newLabel: "New template" },
    ],
  },
  {
    key: "clients",
    label: "Clients",
    defaultPath: "/staff/clients",
    // A first-class directory of every client (list → client hub at
    // /staff/clients/:id, which the prefix match also owns). "New client" opens
    // a standalone create drawer on the list. Archive is a workbench for
    // retiring wound-down clients (longest-match wins, so /staff/clients/archive
    // activates its own sub, /staff/clients/:id the Clients sub).
    subs: [
      { label: "Clients", path: "/staff/clients", newLabel: "New client" },
      // Builder firms (Builders Build Brief 01, D10): the directory + hub at
      // /staff/builders/:id, sharing this menu with clients.
      { label: "Builders", path: "/staff/builders", newLabel: "New builder" },
      { label: "Archive", path: "/staff/clients/archive" },
    ],
  },
  {
    key: "jobs",
    label: "Jobs",
    defaultPath: "/staff/jobs",
    // /staff/jobs covers the job-detail route /staff/jobs/:id too. Open
    // applications is a cross-job, read-only feed of in-flight applications; On
    // Hold lists held jobs (hidden from the main list) with a resume action;
    // Archive is a workbench for retiring completed-and-settled jobs (longest-
    // match wins, so /staff/jobs/open, /staff/jobs/on-hold and /staff/jobs/archive
    // activate their own subs, /staff/jobs/:id the Jobs sub).
    subs: [
      { label: "Jobs", path: "/staff/jobs", newLabel: "New job" },
      { label: "Open applications", path: "/staff/jobs/open" },
      { label: "Order book", path: "/staff/jobs/order-book" },
      { label: "On Hold", path: "/staff/jobs/on-hold" },
      { label: "Archive", path: "/staff/jobs/archive" },
    ],
  },
  {
    key: "letters",
    label: "Letters",
    defaultPath: "/staff/letters",
    // Read + triage only; the unread badge is rendered from the unread context.
    subs: [{ label: "Inbox", path: "/staff/letters" }],
  },
  {
    key: "tasks",
    label: "Tasks",
    // The Board is the default landing view; the due-date List is the secondary
    // view. Both are the same working set; "New task" opens a create drawer on
    // either (optional job link via search).
    defaultPath: "/staff/tasks/board",
    subs: [
      { label: "Board", path: "/staff/tasks/board", newLabel: "New task" },
      { label: "List", path: "/staff/tasks", newLabel: "New task" },
    ],
  },
  {
    key: "invoices",
    label: "Invoices",
    defaultPath: "/staff/invoices",
    // Cross-job dashboard. The list is read-only; "New invoice" opens a
    // job-picker → per-job create flow.
    subs: [{ label: "Invoices", path: "/staff/invoices", newLabel: "New invoice" }],
  },
  {
    key: "feedback",
    label: "Feedback",
    defaultPath: "/staff/feedback",
    // Cross-job Feefo review tracking. Open requests = sent < 30 days ago with no
    // response yet; Feedback received = every review to date. "New feedback
    // request" opens a job-picker → confirm-send flow, available from both views.
    subs: [
      { label: "Open requests", path: "/staff/feedback", newLabel: "New feedback request" },
      { label: "Feedback received", path: "/staff/feedback/received", newLabel: "New feedback request" },
    ],
  },
  {
    key: "review",
    label: "Review",
    defaultPath: "/staff/review",
    // Reconciliation queues: Applications (unmatched + duplicate apps, link +
    // merge), Jobs (auto-created shell-job cleanup / job merge) and Letters
    // (duplicate letters from double-ingest). No create.
    // ADMIN ONLY — merges are destructive and hard to unpick, so the whole
    // section is admin-gated. It is the only such section in the console.
    adminOnly: true,
    subs: [
      { label: "Applications", path: "/staff/review" },
      { label: "Jobs", path: "/staff/review/jobs" },
      { label: "Letters", path: "/staff/review/letters" },
    ],
  },
];

/** The sections a given role may see. Staff get everything except the
 *  admin-only ones; admins get the lot. */
export function visibleSections(isAdmin: boolean): NavSection[] {
  return isAdmin ? NAV_SECTIONS : NAV_SECTIONS.filter((s) => !s.adminOnly);
}

/** Segment-aware prefix match: "/staff/quotes" matches "/staff/quotes" and
 *  "/staff/quotes/1" but not "/staff/quotesX". */
function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function prefixesFor(section: NavSection): string[] {
  return section.matchPrefixes ?? section.subs.map((s) => s.path);
}

/** The section whose any prefix the pathname starts with (null if none).
 *  Pass the role's visible sections so an admin-only prefix never activates a
 *  section the user cannot see. */
export function activeSection(
  pathname: string,
  sections: NavSection[] = NAV_SECTIONS,
): NavSection | null {
  return sections.find((s) => prefixesFor(s).some((p) => matchesPrefix(pathname, p))) ?? null;
}

/** The best-matching sub within a section (longest matching path), falling back
 *  to the section's first sub. */
export function activeSub(section: NavSection, pathname: string): SubLink | null {
  let best: SubLink | null = null;
  for (const sub of section.subs) {
    if (matchesPrefix(pathname, sub.path) && (!best || sub.path.length > best.path.length)) {
      best = sub;
    }
  }
  return best ?? section.subs[0] ?? null;
}
